// frontend/src/api/fetchWithAuth.js
import { refreshAccessToken } from './authApi';

const BASE_URL = process.env.REACT_APP_API_URL || '/api';

let refreshPromise = null;

const getRefreshPromise = () => {
  if (!refreshPromise) {
    refreshPromise = refreshAccessToken().finally(() => {
      refreshPromise = null;
    });
  }
  return refreshPromise;
};

// 응답 상태코드별 공통 안내 메시지
const STATUS_MESSAGES = {
  400: '잘못된 요청입니다.',
  403: '권한이 없습니다.',
  404: '요청하신 정보를 찾을 수 없습니다.',
  413: '업로드 용량이 너무 큽니다. 파일 크기를 줄여주세요.',
  429: '요청이 너무 많습니다. 잠시 후 다시 시도해주세요.',
  500: '서버 오류가 발생했습니다.',
  502: '서버에 연결할 수 없습니다. 잠시 후 다시 시도해주세요.',
  503: '서버 점검 중입니다. 잠시 후 다시 시도해주세요.',
  504: '서버 응답이 지연되고 있습니다. 잠시 후 다시 시도해주세요.',
};

// 응답을 안전하게 JSON으로 파싱 (JSON이 아니면 null)
const safeParseJson = async (res) => {
  const text = await res.text();
  if (!text) return null;
  try {
    return JSON.parse(text);
  } catch {
    return null; // HTML 에러페이지 등 JSON이 아닌 응답
  }
};

/**
 * 인증이 필요한 API 호출 공통 wrapper.
 * - Authorization 헤더 자동 첨부
 * - 401 응답 시 토큰 1회 자동 갱신 후 재시도
 * - 재시도도 401이면 로그인 화면으로 강제 이동(세션 만료)
 * - 어떤 경우에도 예외를 던지지 않고 { success, message } 형태로 반환
 */
export const fetchWithAuth = async (path, options = {}) => {
  const buildHeaders = () => {
    const isFormData = options.body instanceof FormData;
    return {
      ...(isFormData ? {} : { 'Content-Type': 'application/json' }),
      Authorization : `Bearer ${localStorage.getItem('token')}`,
      ...(options.headers || {}),
    };
  };

  let res;
  try {
    res = await fetch(`${BASE_URL}${path}`, { ...options, headers: buildHeaders(), cache: 'no-store' });
  } catch (networkErr) {
    return { success: false, message: '네트워크 연결을 확인해주세요.' };
  }

  if (res.status === 401) {
    const newToken = await getRefreshPromise();

    if (newToken) {
      try {
        res = await fetch(`${BASE_URL}${path}`, { ...options, headers: buildHeaders(), cache: 'no-store' });
      } catch {
        return { success: false, message: '네트워크 연결을 확인해주세요.' };
      }
    }

    if (!newToken || res.status === 401) {
      window.dispatchEvent(new CustomEvent('session-expired'));
      return { success: false, message: '세션이 만료되었습니다. 다시 로그인해주세요.' };
    }
  }

  const data = await safeParseJson(res);

  if (!res.ok) {
    // 서버가 JSON으로 에러 메시지를 내려준 경우 그걸 우선 사용
    const serverMessage = data?.message;
    return {
      success: false,
      message: serverMessage || STATUS_MESSAGES[res.status] || `요청 처리 중 오류가 발생했습니다. (${res.status})`,
    };
  }

  if (data === null) {
    // 200인데 JSON이 아닌 이상한 응답 (거의 없지만 방어)
    return { success: false, message: '서버 응답을 처리할 수 없습니다.' };
  }

  return data;
};